import {
  BaseModel,
  EventWatchable,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import { BroadcastEventType, ResourceType } from '@cotera/api';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { isActiveRunStatus } from './workflow-run-status';
import {
  normalizeRun,
  EMPTY_WORKFLOW_RUN_COST,
  type WorkflowRunDetail,
  type WorkflowRunGraph,
} from './workflow-run.types';

type ResourceUpdatedFilterPayload = {
  resourceType?: string;
  resourceId?: string;
};

/** Self-heal cadence (ms) so an in-flight run converges if a broadcast is missed. */
const WORKFLOW_RUN_RECONCILE_MS = 3_000;

type WorkflowRunModelProps = {
  workflowId: string;
  runId: string;
  orgId: string;
  client: Pick<TenantedClient, 'resource'>;
  queryClient: QueryClient;
};

/**
 * Single-run view model (the per-run analogue of {@link WorkflowModel}). Holds
 * event watchables for the run detail and its graph, refreshed on the workflow's
 * `ResourceUpdated` broadcasts and a reconcile poll, plus a derived
 * `isRunning` flag. Scoped per run and read by the run detail page, the run
 * graph, and the step stack view.
 */
export class WorkflowRunModel extends BaseModel {
  readonly workflowId: string;
  readonly runId: string;
  readonly runWatchable: ReadonlyEventWatchable<WorkflowRunDetail | null>;
  readonly graphWatchable: ReadonlyEventWatchable<WorkflowRunGraph | null>;
  readonly isRunningWatchable: ReadonlyWatchable<boolean>;
  readonly workflowNameWatchable: ReadonlyEventWatchable<string | null>;

  constructor(props: WorkflowRunModelProps) {
    super(`workflow-run:${props.runId}`);
    const { workflowId, runId, orgId, client, queryClient } = props;
    this.workflowId = workflowId;
    this.runId = runId;

    const matchesWorkflow = (p: ResourceUpdatedFilterPayload): boolean =>
      p?.resourceType === 'workflow' && p?.resourceId === workflowId;

    // Seed run + graph from the React Query cache so a remount renders the
    // previously loaded run immediately (no loading flash); `runOnMount` then
    // refetches in the background and each `fn` writes back through for next
    // time. The reconcile poll keeps an in-flight run converging.
    const runKey: QueryKey = queryKeys.workflow.run({
      orgId,
      workflowId,
      runId,
    });
    const graphKey: QueryKey = queryKeys.workflow.runGraph({
      orgId,
      workflowId,
      runId,
    });

    this.runWatchable = EventWatchable.for<
      WorkflowRunDetail | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      reconcile: { cadence: WORKFLOW_RUN_RECONCILE_MS },
      debounceMs: 300,
      initialValue: queryClient.getQueryData<WorkflowRunDetail>(runKey) ?? null,
      filter: matchesWorkflow,
      equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
      fn: async (_payload, get) => {
        const res = await client.resource.workflow.getRun({
          workflowId,
          runId,
        });
        if (res.isErr()) {
          return get(this.runWatchable);
        }
        const detail = res.value;
        const value: WorkflowRunDetail = {
          ...normalizeRun(detail),
          retriedByRunId: detail.retriedByRunId ?? null,
          input: detail.input ?? null,
          outputs: detail.outputs ?? [],
          cost: detail.cost ?? EMPTY_WORKFLOW_RUN_COST,
        };
        queryClient.setQueryData(runKey, value);
        return value;
      },
    });

    this.graphWatchable = EventWatchable.for<
      WorkflowRunGraph | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      reconcile: { cadence: WORKFLOW_RUN_RECONCILE_MS },
      debounceMs: 300,
      initialValue:
        queryClient.getQueryData<WorkflowRunGraph>(graphKey) ?? null,
      filter: matchesWorkflow,
      equalityFn: (a, b) => JSON.stringify(a) === JSON.stringify(b),
      fn: async (_payload, get) => {
        const res = await client.resource.workflow.getRunGraph({
          workflowId,
          runId,
        });
        if (res.isErr()) {
          return get(this.graphWatchable);
        }
        queryClient.setQueryData(graphKey, res.value);
        return res.value;
      },
    });

    this.workflowNameWatchable = EventWatchable.for<
      string | null,
      ResourceUpdatedFilterPayload
    >({
      event: BroadcastEventType.ResourceUpdated,
      runOnMount: true,
      debounceMs: 500,
      initialValue: null,
      filter: matchesWorkflow,
      equalityFn: (a, b) => a === b,
      fn: async (_payload, get) => {
        const res = await client.resource.get({
          type: ResourceType.Workflow,
          id: workflowId,
        });
        if (res.isErr() || res.value.type !== 'workflow') {
          return get(this.workflowNameWatchable);
        }
        return res.value.workflow.name;
      },
    });

    this.isRunningWatchable = Watchable.from((get) =>
      isActiveRunStatus(get(this.runWatchable)?.status)
    );
  }

  /** Force-refetch the run and its graph (e.g. right after cancelling it). */
  refresh(): void {
    void this.runWatchable.refresh();
    void this.graphWatchable.refresh();
  }

  dispose(): void {
    this.runWatchable.unsubscribe();
    this.graphWatchable.unsubscribe();
    this.workflowNameWatchable.unsubscribe();
  }
}

export const useWorkflowRunModel = (): WorkflowRunModel => {
  const model = useInScopeModel(WorkflowRunModel);
  if (model === null) {
    throw new Error('WorkflowRunModel not found in scope');
  }
  return model;
};

/** Null when not on a run-detail surface (e.g. the editor canvas). */
export const useWorkflowRunModelOrNull = (): WorkflowRunModel | null =>
  useInScopeModel(WorkflowRunModel);
